import { Check, ChevronDown, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Badge } from './badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command-palette';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface ComboboxOption {
  value: string;
  label: string;
  icon?: ReactNode;
  /** A muted second line under the label in the list (e.g. what a role lets someone do). */
  description?: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  multiple?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  emptyText = 'No results',
  multiple = true,
  disabled,
  id,
  className,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value],
  );

  const toggle = (v: string) => {
    if (!multiple) {
      onChange([v]);
      setOpen(false);
      return;
    }
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const remove = (v: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((x) => x !== v));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            'flex min-h-9 w-full items-center justify-between rounded-sm border border-border bg-bg-inset',
            'px-3 py-1 text-body text-text-primary',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'hover:border-border-strong transition-colors',
            className,
          )}
        >
          {selected.length === 0 ? (
            <span className="text-text-muted">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selected.map((opt) => (
                <Badge key={opt.value} variant="purple" className="gap-1">
                  {opt.label}
                  {multiple ? (
                    <button
                      onClick={(e) => remove(opt.value, e)}
                      className="ml-0.5 rounded-full hover:bg-accent-200/50"
                      aria-label={`Remove ${opt.label}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  ) : null}
                </Badge>
              ))}
            </div>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}`} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = value.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    // Search finds a role by what it does, not only by its name.
                    keywords={opt.description ? [opt.description] : undefined}
                    onSelect={() => toggle(opt.value)}
                    // A label longer than the popover wraps; a fixed row height then laid each option over the next
                    // (the lead drawer's live-car list, Hassan 2026-10-01). Short labels keep the 44px row.
                    className="h-auto min-h-11 py-2"
                  >
                    <Check
                      className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100 text-accent-500' : 'opacity-0')}
                    />
                    {opt.icon}
                    {opt.description ? (
                      <span className="flex min-w-0 flex-col">
                        <span>{opt.label}</span>
                        <span className="text-caption text-text-muted">{opt.description}</span>
                      </span>
                    ) : (
                      opt.label
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
